import React from 'react';
import { observer } from 'mobx-react';

import { colors } from 'app/variables';
import {
  EmailIcon,
  PhoneIcon,
  LocationIcon,
  ClockIcon,
} from 'app/commonComponents/icons';
import vouchersViewStore from 'modules/ticketPublicPage/stores/vouchers/view';

import {
  AddressContactContainer,
  ContactContainer,
  ContactLink,
  DaysTxt,
  FacilityName,
  FacilityNameContainer,
  HoursTxt,
  InfoBlockContainer,
  InlineFlexContainer,
  InlineIcon,
  InlineName,
  OpeningHoursList,
} from './Info.styles';

const formatWeekdays = (weekdays) => {
  const visualResult = [];
  const ariaResult = [];

  const localeData = moment().localeData();
  const weekdaysMin = [
    ...localeData._weekdaysMin.slice(1),
    localeData._weekdaysMin[0],
  ];
  const weekdaysFull = [
    ...localeData.weekdays().slice(1),
    localeData.weekdays()[0],
  ];

  for (let i = 0; i < weekdays.length; i++) {
    let start = weekdays[i];
    let end = start;

    while (i < weekdays.length - 1 && weekdays[i + 1] === end + 1) {
      end = weekdays[++i];
    }

    const isRange = end - start >= 2;

    visualResult.push(
      isRange
        ? `${weekdaysMin[start]} - ${weekdaysMin[end]}`
        : weekdaysMin[start] + (end !== start ? ', ' + weekdaysMin[end] : '')
    );

    ariaResult.push(
      isRange
        ? i18n.sprintf(
            i18n.pgettext('from day x to day y', `%s to %s`),
            weekdaysFull[start],
            weekdaysFull[end]
          )
        : weekdaysFull[start] + (end !== start ? ', ' + weekdaysFull[end] : '')
    );

    if (i < weekdays.length - 1) {
      visualResult.push(', ');
      ariaResult.push(', ');
    }
  }

  return {
    visual: visualResult.join(''),
    aria: ariaResult.join(''),
  };
};

const getHourTimeRange = (hour) => {
  return `${moment(hour.begins, 'HH:mm').format('LT')} - ${moment(
    hour.ends,
    'HH:mm'
  ).format('LT')}`;
};

const Info = observer(() => {
  const { facility, prefersEmail, prefersPhone } = vouchersViewStore;
  const { website } = facility;
  const facilityHours =
    facility.opening_hours && facility.opening_hours.length > 0
      ? facility.visual_opening_hours &&
        facility.visual_opening_hours.length > 0
        ? facility.visual_opening_hours
        : facility.opening_hours
      : null;

  return (
    <InfoBlockContainer>
      <FacilityNameContainer>
        <FacilityName
          $asButton={!!website}
          href={!!website ? website : null}
          target="_blank"
        >
          {facility.name}
        </FacilityName>
      </FacilityNameContainer>
      <ContactContainer>
        {facilityHours && (
          <InlineFlexContainer>
            <InlineIcon>
              <ClockIcon style={{ color: colors.dustyGray }} />
            </InlineIcon>
            <InlineName>
              <div style={{ fontWeight: 800 }}>
                {i18n.gettext('Opening times')}
              </div>
              {facilityHours.map((hour, index) => (
                <OpeningHoursList key={index}>
                  <DaysTxt aria-label={formatWeekdays(hour.weekdays).aria}>
                    <span aria-hidden="true">
                      {formatWeekdays(hour.weekdays).visual}
                    </span>
                  </DaysTxt>
                  <HoursTxt>{getHourTimeRange(hour)}</HoursTxt>
                </OpeningHoursList>
              ))}
            </InlineName>
          </InlineFlexContainer>
        )}
        <AddressContactContainer
          $containsEmail={prefersEmail && facility.email}
        >
          {(facility.street || facility.zip_code || facility.city) && (
            <InlineFlexContainer style={{ alignItems: 'center' }}>
              <InlineIcon>
                <LocationIcon style={{ color: colors.dustyGray }} />
              </InlineIcon>
              <InlineName>
                <div>
                  {facility.street}, {facility.zip_code} {facility.city}
                </div>
              </InlineName>
            </InlineFlexContainer>
          )}
          {prefersPhone && facility.phone ? (
            <InlineFlexContainer style={{ alignItems: 'center' }}>
              <InlineIcon>
                <PhoneIcon style={{ stroke: colors.dustyGray }} />
              </InlineIcon>
              <InlineName>
                <ContactLink href={`tel:${facility.phone}`}>
                  {facility.phone}
                </ContactLink>
              </InlineName>
            </InlineFlexContainer>
          ) : null}
          {prefersEmail && facility.email ? (
            <InlineFlexContainer style={{ alignItems: 'center' }}>
              <InlineIcon>
                <EmailIcon style={{ stroke: colors.dustyGray }} />
              </InlineIcon>
              <InlineName>
                <ContactLink href={`mailto:${facility.email}`}>
                  {facility.email}
                </ContactLink>
              </InlineName>
            </InlineFlexContainer>
          ) : null}
        </AddressContactContainer>
      </ContactContainer>
    </InfoBlockContainer>
  );
});

export default Info;
